import { useRef } from 'react';
import { ScienceOutlined } from '@mui/icons-material';
import { ConfirmModal, FormActions, FormCard, FormSectionTitle, TabBar } from '@practium/ui';
import { formatPeriodLabel } from '../../constants/maintenance.constants';
import { useMixturesPanel } from '../../hooks/useMixturesPanel';
import type { FertilizationPlanAdvice, MaintenanceMixture } from '../../types';
import { PanelActions } from '../PanelActions/PanelActions';
import tabStyles from '../Tabs/TabPanel.module.css';
import { MixtureCard } from './MixtureCard';
import { MixtureCardList } from './MixtureCardList';
import { MixtureComposer } from './MixtureComposer';
import { MixtureDemandSection } from './MixtureDemandSection';
import { MixtureMonthSummary } from './MixtureMonthSummary';
import { MixtureReviewFooter } from './MixtureReviewFooter';
import { MixtureSettingsSection } from './MixtureSettingsSection';
import styles from './MixturesPanel.module.css';

interface MixturesPanelProps {
	advice: FertilizationPlanAdvice;
	onSaved: () => void;
}

export const MixturesPanel = ({ advice, onSaved }: MixturesPanelProps) => {
	const panel = useMixturesPanel({ advice, onSaved });
	const monthLabel = formatPeriodLabel(panel.activeMonth).toLocaleLowerCase('pt-BR');

	const composerTitleRef = useRef<HTMLHeadingElement>(null);

	const handleEdit = (mixture: MaintenanceMixture) => {
		panel.composer.load(mixture);
		composerTitleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const mixtureCards = panel.list.visibleMixtures.map((mixture) => (
		<MixtureCard
			key={mixture._id}
			mixture={mixture}
			position={panel.positionOf(mixture)}
			usage={panel.usageOf(mixture)}
			sectorName={panel.showSectorName ? mixture.sectorName : undefined}
			isEditing={panel.composer.editingId === mixture._id}
			onEdit={panel.isView ? undefined : () => handleEdit(mixture)}
			onConfirm={panel.isView ? undefined : () => panel.handleConfirm(mixture._id)}
		/>
	));

	return (
		<div className={`${tabStyles.panel} ${styles.container}`}>
			<FormCard icon={<ScienceOutlined />} title="Misturas">
				<div className={tabStyles.form}>
					<MixtureSettingsSection
						form={panel.settingsForm}
						errors={panel.settingsErrors}
						isView={panel.isView}
						onChange={panel.setSetting}
					/>

					{panel.hasMixtures && (
						<MixtureDemandSection
							nutrientDemand={panel.nutrientDemand}
							systemRecommendation={panel.systemRecommendation}
						/>
					)}

					{panel.hasMixtures && (
						<div className={tabStyles.section}>
							<FormSectionTitle>Planejamento de misturas</FormSectionTitle>
							<TabBar
								tabs={panel.months.map((month) => ({
									key: month,
									label: formatPeriodLabel(month),
								}))}
								activeTab={panel.activeMonth}
								onTabChange={panel.setSelectedMonth}
							/>
							<MixtureMonthSummary summary={panel.summary} />
							{panel.isView ? (
								<MixtureCardList list={panel.list}>{mixtureCards}</MixtureCardList>
							) : (
								<div className={styles.workspace}>
									<h3 ref={composerTitleRef} className={styles.workspaceTitle}>
										Elaboração da Mistura
									</h3>
									<MixtureComposer
										composer={panel.composer}
										usage={panel.composerUsage}
										vbox={panel.savedSettings?.vbox ?? 0}
										productOptions={panel.productOptions}
										comboProductOptions={panel.comboProductOptions}
										isPending={panel.isPending}
										onSubmit={panel.handleSubmitComposer}
									/>
									<div className={styles.monthHeader}>
										<h3 className={styles.monthTitle}>Planejamento de {monthLabel}</h3>
										<span className={styles.monthCount}>
											<strong>
												{panel.confirmedCount} de {panel.monthMixtures.length}
											</strong>{' '}
											verificadas
										</span>
									</div>
									<MixtureCardList list={panel.list}>{mixtureCards}</MixtureCardList>
									<MixtureReviewFooter
										total={panel.reviewMixtures.length}
										pendingCount={panel.reviewPendingIds.length}
										isFiltered={panel.list.isFiltered}
										isPending={panel.isPending}
										onConfirmAll={panel.handleConfirmAll}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			</FormCard>

			{!panel.isView && (
				<PanelActions>
					<FormActions
						onSecondary={panel.canGenerate ? panel.handleGenerate : undefined}
						secondaryText="Elaborar misturas"
						secondaryDisabled={panel.isPending}
						secondaryLoading={panel.isPending}
						onSave={panel.hasMixtures ? panel.handleSave : undefined}
					/>
				</PanelActions>
			)}

			<ConfirmModal
				isOpen={panel.pendingModal.isOpen}
				title={panel.pendingModal.options.title}
				message={panel.pendingModal.options.message}
				confirmText={panel.pendingModal.options.confirmText}
				cancelText={panel.pendingModal.options.cancelText}
				variant={panel.pendingModal.options.variant}
				onConfirm={panel.pendingModal.handleConfirm}
				onCancel={panel.pendingModal.handleCancel}
			/>
		</div>
	);
};
